import { useEffect, useRef, useState } from "react";

/**
 * In-app API reference (v7.1) — gated to superadmin via ProtectedRoute in App.js.
 * Renders the OpenAPI spec with Redoc. The spec is inlined below (no secrets in it),
 * so this page needs no network except the Redoc renderer script.
 *
 * Route (App.js):
 *   <Route path="/dev/api-docs"
 *     element={<ProtectedRoute roles={["superadmin"]}><ApiDocs /></ProtectedRoute>} />
 */

// OpenAPI 3.1 spec for the Solution public API (source of truth: openapi.yaml).
const SPEC = {
  "openapi": "3.1.0",
  "info": {
    "title": "Solution Public API",
    "version": "1.0.0",
    "description": "Programmatic access to the Solution IT service desk, by Arventiq Labs.\n\n**Authentication.** Every request needs an API key, created under\n*Admin \u2192 Developer / integrations*. Send it as either:\n- `Authorization: Bearer sk_live_\u2026`\n- `X-API-Key: sk_live_\u2026`\n\nKeys are scoped: `tickets:read` and/or `tickets:write`. A key belongs to one\ninstitute; all requests are automatically scoped to that institute.\n\n**Webhooks.** Register endpoints under the same settings screen to receive\nsigned event callbacks (see the *webhooks* section below). Each delivery is\nsigned with HMAC-SHA256 in the `X-Solution-Signature` header.\n",
    "contact": {
      "name": "Arventiq Labs",
      "email": "beerappa@fortifyhub.net"
    }
  },
  "servers": [
    {
      "url": "https://solution.arventiqlabs.com/api/v1",
      "description": "Production"
    }
  ],
  "security": [
    {
      "BearerKey": []
    },
    {
      "ApiKeyHeader": []
    }
  ],
  "tags": [
    {
      "name": "Tickets",
      "description": "Create, read and update tickets."
    }
  ],
  "paths": {
    "/tickets": {
      "get": {
        "tags": [
          "Tickets"
        ],
        "summary": "List tickets",
        "description": "Returns the institute's tickets, newest first. Requires `tickets:read`.",
        "parameters": [
          {
            "name": "status",
            "in": "query",
            "required": false,
            "schema": {
              "$ref": "#/components/schemas/Status"
            }
          },
          {
            "name": "limit",
            "in": "query",
            "required": false,
            "schema": {
              "type": "integer",
              "default": 50,
              "maximum": 200,
              "minimum": 1
            }
          }
        ],
        "responses": {
          "200": {
            "description": "A list of tickets.",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "properties": {
                    "tickets": {
                      "type": "array",
                      "items": {
                        "$ref": "#/components/schemas/TicketSummary"
                      }
                    }
                  }
                }
              }
            }
          },
          "401": {
            "$ref": "#/components/responses/Unauthorized"
          },
          "403": {
            "$ref": "#/components/responses/Forbidden"
          }
        }
      },
      "post": {
        "tags": [
          "Tickets"
        ],
        "summary": "Create a ticket",
        "description": "Creates and auto-routes a ticket (least-loaded technician, business-hours\nSLA). Requires `tickets:write`. Provide `requester_email` to attribute it\nto a real requester; otherwise it is attributed to a staff account.\n",
        "requestBody": {
          "required": true,
          "content": {
            "application/json": {
              "schema": {
                "$ref": "#/components/schemas/TicketCreate"
              }
            }
          }
        },
        "responses": {
          "201": {
            "description": "Ticket created.",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "properties": {
                    "ticket": {
                      "type": "object",
                      "properties": {
                        "id": {
                          "type": "integer"
                        },
                        "ticket_no": {
                          "type": "string",
                          "example": "TKT-25-00123"
                        },
                        "status": {
                          "$ref": "#/components/schemas/Status"
                        }
                      }
                    }
                  }
                }
              }
            }
          },
          "401": {
            "$ref": "#/components/responses/Unauthorized"
          },
          "403": {
            "$ref": "#/components/responses/Forbidden"
          },
          "422": {
            "$ref": "#/components/responses/Unprocessable"
          }
        }
      }
    },
    "/tickets/{id}": {
      "parameters": [
        {
          "name": "id",
          "in": "path",
          "required": true,
          "schema": {
            "type": "integer"
          }
        }
      ],
      "get": {
        "tags": [
          "Tickets"
        ],
        "summary": "Get a ticket",
        "description": "Requires `tickets:read`.",
        "responses": {
          "200": {
            "description": "The ticket.",
            "content": {
              "application/json": {
                "schema": {
                  "type": "object",
                  "properties": {
                    "ticket": {
                      "$ref": "#/components/schemas/Ticket"
                    }
                  }
                }
              }
            }
          },
          "401": {
            "$ref": "#/components/responses/Unauthorized"
          },
          "404": {
            "$ref": "#/components/responses/NotFound"
          }
        }
      },
      "patch": {
        "tags": [
          "Tickets"
        ],
        "summary": "Update a ticket",
        "description": "Update `status` and/or `priority`. Requires `tickets:write`.",
        "requestBody": {
          "required": true,
          "content": {
            "application/json": {
              "schema": {
                "$ref": "#/components/schemas/TicketUpdate"
              }
            }
          }
        },
        "responses": {
          "200": {
            "$ref": "#/components/responses/Ok"
          },
          "401": {
            "$ref": "#/components/responses/Unauthorized"
          },
          "404": {
            "$ref": "#/components/responses/NotFound"
          }
        }
      }
    },
    "/tickets/{id}/comments": {
      "parameters": [
        {
          "name": "id",
          "in": "path",
          "required": true,
          "schema": {
            "type": "integer"
          }
        }
      ],
      "post": {
        "tags": [
          "Tickets"
        ],
        "summary": "Add a comment",
        "description": "Adds a public (or internal) comment. Requires `tickets:write`.",
        "requestBody": {
          "required": true,
          "content": {
            "application/json": {
              "schema": {
                "type": "object",
                "required": [
                  "body"
                ],
                "properties": {
                  "body": {
                    "type": "string",
                    "maxLength": 5000
                  },
                  "internal": {
                    "type": "boolean",
                    "default": false,
                    "description": "Internal note (not shown to the requester)."
                  }
                }
              }
            }
          }
        },
        "responses": {
          "201": {
            "$ref": "#/components/responses/Ok"
          },
          "401": {
            "$ref": "#/components/responses/Unauthorized"
          },
          "404": {
            "$ref": "#/components/responses/NotFound"
          }
        }
      }
    }
  },
  "webhooks": {
    "ticket.created": {
      "post": {
        "summary": "A ticket was created",
        "requestBody": {
          "content": {
            "application/json": {
              "schema": {
                "$ref": "#/components/schemas/WebhookEvent"
              }
            }
          }
        },
        "responses": {
          "200": {
            "description": "Return 2xx to acknowledge."
          }
        }
      }
    },
    "ticket.updated": {
      "post": {
        "summary": "A ticket's status or priority changed",
        "requestBody": {
          "content": {
            "application/json": {
              "schema": {
                "$ref": "#/components/schemas/WebhookEvent"
              }
            }
          }
        },
        "responses": {
          "200": {
            "description": "Return 2xx to acknowledge."
          }
        }
      }
    },
    "ticket.resolved": {
      "post": {
        "summary": "A ticket was resolved",
        "requestBody": {
          "content": {
            "application/json": {
              "schema": {
                "$ref": "#/components/schemas/WebhookEvent"
              }
            }
          }
        },
        "responses": {
          "200": {
            "description": "Return 2xx to acknowledge."
          }
        }
      }
    },
    "sla.breached": {
      "post": {
        "summary": "A ticket passed its SLA due time",
        "requestBody": {
          "content": {
            "application/json": {
              "schema": {
                "$ref": "#/components/schemas/WebhookEvent"
              }
            }
          }
        },
        "responses": {
          "200": {
            "description": "Return 2xx to acknowledge."
          }
        }
      }
    }
  },
  "components": {
    "securitySchemes": {
      "BearerKey": {
        "type": "http",
        "scheme": "bearer",
        "description": "Send your API key as a bearer token: `Authorization: Bearer sk_live_\u2026`"
      },
      "ApiKeyHeader": {
        "type": "apiKey",
        "in": "header",
        "name": "X-API-Key"
      }
    },
    "responses": {
      "Ok": {
        "description": "Success.",
        "content": {
          "application/json": {
            "schema": {
              "type": "object",
              "properties": {
                "ok": {
                  "type": "boolean",
                  "example": true
                }
              }
            }
          }
        }
      },
      "Unauthorized": {
        "description": "Missing or invalid API key.",
        "content": {
          "application/json": {
            "schema": {
              "$ref": "#/components/schemas/Error"
            }
          }
        }
      },
      "Forbidden": {
        "description": "The key lacks the required scope.",
        "content": {
          "application/json": {
            "schema": {
              "$ref": "#/components/schemas/Error"
            }
          }
        }
      },
      "NotFound": {
        "description": "Resource not found in this institute.",
        "content": {
          "application/json": {
            "schema": {
              "$ref": "#/components/schemas/Error"
            }
          }
        }
      },
      "Unprocessable": {
        "description": "The request was understood but could not be fulfilled (e.g. no requester could be resolved).",
        "content": {
          "application/json": {
            "schema": {
              "$ref": "#/components/schemas/Error"
            }
          }
        }
      }
    },
    "schemas": {
      "Status": {
        "type": "string",
        "enum": [
          "open",
          "assigned",
          "in_progress",
          "on_hold",
          "resolved",
          "closed",
          "reopened"
        ]
      },
      "Priority": {
        "type": "string",
        "enum": [
          "low",
          "medium",
          "high",
          "urgent"
        ]
      },
      "TicketSummary": {
        "type": "object",
        "properties": {
          "id": {
            "type": "integer"
          },
          "ticket_no": {
            "type": "string",
            "example": "TKT-25-00123"
          },
          "title": {
            "type": "string"
          },
          "status": {
            "$ref": "#/components/schemas/Status"
          },
          "priority": {
            "$ref": "#/components/schemas/Priority"
          },
          "created_at": {
            "type": "string",
            "format": "date-time"
          },
          "due_at": {
            "type": "string",
            "format": "date-time",
            "nullable": true
          },
          "category": {
            "type": "object",
            "nullable": true,
            "properties": {
              "name": {
                "type": "string"
              }
            }
          }
        }
      },
      "Ticket": {
        "allOf": [
          {
            "$ref": "#/components/schemas/TicketSummary"
          },
          {
            "type": "object",
            "properties": {
              "description": {
                "type": "string"
              },
              "resolved_at": {
                "type": "string",
                "format": "date-time",
                "nullable": true
              },
              "assignee": {
                "type": "object",
                "nullable": true,
                "properties": {
                  "name": {
                    "type": "string"
                  }
                }
              }
            }
          }
        ]
      },
      "TicketCreate": {
        "type": "object",
        "required": [
          "title"
        ],
        "properties": {
          "title": {
            "type": "string",
            "maxLength": 200
          },
          "description": {
            "type": "string",
            "maxLength": 5000
          },
          "priority": {
            "$ref": "#/components/schemas/Priority"
          },
          "category_id": {
            "type": "integer",
            "description": "Optional; defaults to the first active category."
          },
          "requester_email": {
            "type": "string",
            "format": "email",
            "description": "Optional; attributes the ticket to this user if they exist."
          }
        }
      },
      "TicketUpdate": {
        "type": "object",
        "properties": {
          "status": {
            "$ref": "#/components/schemas/Status"
          },
          "priority": {
            "$ref": "#/components/schemas/Priority"
          }
        }
      },
      "WebhookEvent": {
        "type": "object",
        "properties": {
          "event": {
            "type": "string",
            "example": "ticket.resolved"
          },
          "at": {
            "type": "string",
            "format": "date-time"
          },
          "data": {
            "type": "object",
            "properties": {
              "id": {
                "type": "integer"
              },
              "ticket_no": {
                "type": "string"
              },
              "title": {
                "type": "string"
              },
              "status": {
                "$ref": "#/components/schemas/Status"
              }
            }
          }
        }
      },
      "Error": {
        "type": "object",
        "properties": {
          "error": {
            "type": "string",
            "example": "Invalid or missing API key."
          }
        }
      }
    }
  }
};

const REDOC_SRC = "https://cdn.redoc.ly/redoc/latest/bundles/redoc.standalone.js";

export default function ApiDocs() {
  const ref = useRef(null);
  const [status, setStatus] = useState("loading"); // loading | ready | error

  useEffect(() => {
    let cancelled = false;

    const init = () => {
      if (cancelled || !window.Redoc || !ref.current) return;
      try {
        window.Redoc.init(SPEC, {
          scrollYOffset: 0,
          theme: {
            colors: { primary: { main: "#0654FC" } },
            typography: {
              fontFamily: "Inter, system-ui, sans-serif",
              headings: { fontFamily: "Inter, system-ui, sans-serif", fontWeight: "700" },
              code: { fontSize: "13px" },
            },
            sidebar: { backgroundColor: "#0D1A3A", textColor: "#dfe6f5" },
          },
        }, ref.current);
        setStatus("ready");
      } catch (e) {
        setStatus("error");
      }
    };

    if (window.Redoc) { init(); return () => { cancelled = true; }; }

    let script = document.querySelector(`script[src="${REDOC_SRC}"]`);
    if (!script) {
      script = document.createElement("script");
      script.src = REDOC_SRC;
      script.async = true;
      script.onload = init;
      script.onerror = () => !cancelled && setStatus("error");
      document.body.appendChild(script);
    } else {
      script.addEventListener("load", init);
    }
    return () => { cancelled = true; };
  }, []);

  return (
    <div style={{ minHeight: "100vh", background: "#fff" }}>
      <header style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 24px", borderBottom: "1px solid #eef1f7", position: "sticky", top: 0, background: "#fff", zIndex: 20 }}>
        <div style={{ width: 24, height: 24, borderRadius: 6, background: "linear-gradient(135deg,#0654FC,#1BA85C)" }} />
        <b style={{ color: "#0D1A3A" }}>Solution API <span style={{ color: "#1BA85C" }}>Docs</span></b>
        <span style={{ marginLeft: "auto", fontSize: 12, color: "#5b6b8c", background: "#f4f7fc", border: "1px solid #e6eaf2", padding: "4px 10px", borderRadius: 20 }}>
          Superadmin only · v1
        </span>
      </header>

      {status === "loading" && <div style={{ padding: 40, color: "#5b6b8c" }}>Loading API reference…</div>}
      {status === "error" && (
        <div style={{ padding: 40, color: "#c0392b" }}>
          Couldn't load the documentation renderer. Check your connection, or self-host the Redoc script.
        </div>
      )}
      <div ref={ref} />
    </div>
  );
}
